import { useRef, useState } from 'react';
import { Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CaseResponseDto } from '@bandido/shared/dtos';
import { useGetUsersByAccess } from '@frontend/features/users/queries';
import type { UserSelectorTableItem } from '@frontend/features/users/components/UserSelectorModal';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { TablePagination } from '@frontend/features/common/components/Table/TablePagination';
import { ROWS_PER_PAGE_OPTIONS } from '@frontend/features/common/components/Table';
import { useMobileViewport } from '@frontend/features/common/hooks/useMobileViewport';

interface Props {
  caseData: CaseResponseDto;
  onClose: () => void;
  onSelect: (agent: UserSelectorTableItem) => Promise<boolean>;
}

export const CaseAgentSelectorMobile = ({ caseData, onClose, onSelect }: Props) => {
  const { t } = useTranslation();
  const viewport = useMobileViewport(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [choice, setChoice] = useState<UserSelectorTableItem>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const inFlight = useRef(false);
  const { data, isLoading, isFetching, isError, refetch } = useGetUsersByAccess({
    agencyId: String(caseData.agencyId),
    currentPage: page,
    pageSize,
    search,
  });
  const options: UserSelectorTableItem[] = (data?.items || []).map((user) => ({
    id: user.id,
    clientId: user.clientId,
    name: [user.firstName, user.lastName].filter(Boolean).join(' '),
    agency: user.agencyName,
    phone: '',
  }));
  const save = async () => {
    if (!choice || choice.clientId === caseData.agentId || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      const saved = await onSelect(choice);
      setSaveError(!saved);
    } catch (_error) {
      setSaveError(true);
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  const close = () => {
    if (!inFlight.current) onClose();
  };
  return (
    <Dialog
      open
      fullScreen
      onClose={close}
      aria-labelledby="case-agent-selector-title"
      PaperProps={{
        sx: {
          position: 'fixed',
          ...viewport,
          maxHeight: 'none',
          pt: 'env(safe-area-inset-top)',
          pb: 'env(safe-area-inset-bottom)',
          '& input, & .MuiSelect-select': { fontSize: 16 },
          '& button': { minHeight: 44, minWidth: 44 },
        },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} p={2} flexShrink={0}>
        <Typography component="h2" variant="h6" id="case-agent-selector-title" flex={1}>
          {t('cases.view.selectAgent')}
        </Typography>
        <IconButton aria-label={t('common.close')} onClick={close} disabled={saving}>
          <Close />
        </IconButton>
      </Stack>
      <Box
        component="fieldset"
        disabled={saving}
        sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', border: 0, m: 0, px: 2 }}
      >
        <SearchInput
          value={search}
          placeholder={t('users.search')}
          onSearch={(value) => {
            setSearch(value);
            setPage(1);
          }}
        />
        {isFetching && <LinearProgress sx={{ mt: 1 }} />}
        {isError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={<Button onClick={() => refetch()}>{t('common.refresh')}</Button>}
          >
            {t('cases.view.agentLoadError')}
          </Alert>
        )}
        <RadioGroup
          aria-label={t('cases.view.agent')}
          value={choice?.clientId ?? caseData.agentId ?? ''}
          onChange={(_, value) => setChoice(options.find((option) => option.clientId === value))}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.clientId}
              value={option.clientId}
              disabled={saving}
              control={<Radio />}
              sx={{
                mx: 0,
                py: 1,
                minHeight: 48,
                '&& .MuiFormControlLabel-label': { minWidth: 0, overflowWrap: 'anywhere', textTransform: 'none' },
              }}
              label={
                <Box>
                  <Typography>{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.agency}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
        {!isLoading && !isError && !options.length && <Typography py={3}>{t('common.noData')}</Typography>}
        <TablePagination
          compact
          totalRows={data?.count || 0}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onChangePage={setPage}
          onChangeRowsPerPage={(value) => {
            setPageSize(value);
            setPage(1);
          }}
        />
      </Box>
      <Box p={2} sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', overflowWrap: 'anywhere' }}>
        <Typography variant="body2" mb={1}>
          {choice?.name ||
            [caseData.agentFirstName, caseData.agentLastName].filter(Boolean).join(' ') ||
            t('common.unassigned')}
        </Typography>
        {saveError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {t('cases.update.error')}
          </Alert>
        )}
        <Button
          fullWidth
          variant="contained"
          onClick={save}
          disabled={saving || !choice || choice.clientId === caseData.agentId}
        >
          {t('cases.view.saveAssignment')}
        </Button>
      </Box>
    </Dialog>
  );
};
